/**
 * TD-2465 — shared pill-filter store logic.
 *
 * The clickable-pill keyword filter behaves identically on the SearchResults
 * page and the Projects results view, so the toggle / mode / subset logic lives
 * here once and both views' MobX stores reuse it (via `createPillFilterActions`
 * for the actions and `pickPillColumnFilters` for the chip-rail subset). The
 * pure helpers are also directly unit-testable, unlike the previous
 * store-embedded copies.
 *
 * Keeping this in one place is what keeps the two surfaces from duplicating the
 * ~20-line toggle block (SonarCloud "duplicated lines on new code").
 */

import { KEYWORD_CLOUD_COLUMNS } from './columnFilterTypes';
import { normalizeKeywordQuery } from '../../../../utils/keywordQuery';

/**
 * Match mode a freshly-created keyword filter starts in.
 *
 * The unified-filter model specifies Any/OR (TD-2561), which is the default
 * here. The current pill toggle ships All/AND, though, and it shares this
 * helper — so its store passes `defaultMode: 'and'` until the flag is on.
 * Flipping it for everyone at once would widen result sets before the new
 * filter UI exists to explain the change.
 *
 * Kept as a parameter rather than read from the environment so this module
 * stays pure and env-free.
 */
const DEFAULT_KEYWORD_MODE = 'or';

/**
 * Compute the next value for a column's keyword-cloud filter when a pill is
 * clicked. Multiple pills in the same column combine per the column's current
 * match mode (see `defaultKeywordMode` for a fresh filter); across columns the
 * engine already ANDs.
 *
 * Clears the keyword the HIGHLIGHT is reacting to, not a byte-identical one:
 * ChipsList lights a pill up case-insensitively (and ignoring the quotes on a
 * quoted keyword) via `normalizeKeywordQuery`, so a raw `Set.has()` would miss a
 * stored case variant and append a near-duplicate instead of removing it —
 * leaving the pill lit and unclearable from its own row (TD-2465 fix). Both
 * views share this one implementation so the toggle and the highlight can't
 * drift apart again.
 *
 * @returns the updated `{ keywords: Set, mode }`, `null` to clear the column
 *   (its last value was removed), or `undefined` for a no-op (empty/absent
 *   value) so the caller can skip the write.
 */
export function nextPillToggleValue(
  columnFilters,
  columnKey,
  value,
  defaultMode = DEFAULT_KEYWORD_MODE
) {
  if (value == null || value === '') return undefined;
  return toggleKeywordValue(columnFilters[columnKey], value, defaultMode);
}

/**
 * Toggle a single keyword in (or out of) a keyword-cloud filter VALUE — the
 * `{ keywords, mode }` object for one column — preserving its match mode. The
 * single-value core shared by the pill toggle (`nextPillToggleValue`) and the
 * keyword-cloud editor, so a pill click and an editor checkbox behave the same.
 *
 * Adds the keyword if absent, else removes the stored variant the highlight is
 * reacting to (case-/quote-insensitive via `normalizeKeywordQuery`, so a stored
 * case or quoted variant is cleared rather than a near-duplicate appended).
 *
 * @returns the updated `{ keywords: Set, mode }`, or `null` when the last
 *   keyword was removed (caller clears the column). A fresh filter takes
 *   `defaultKeywordMode()`.
 */
export function toggleKeywordValue(value, keyword, defaultMode = DEFAULT_KEYWORD_MODE) {
  const keywords = new Set(value?.keywords ?? []);
  const target = normalizeKeywordQuery(keyword).needle;
  const stored = [...keywords].find((raw) => normalizeKeywordQuery(raw).needle === target);
  if (stored === undefined) {
    keywords.add(keyword);
  } else {
    keywords.delete(stored);
  }
  const mode = value?.mode || defaultMode;
  return keywords.size ? { keywords, mode } : null;
}

/**
 * Compute the next value when flipping a column between AND ('and') and OR
 * ('or'), preserving the clicked values. Returns `undefined` (no-op) when the
 * column has no pills.
 */
export function nextPillModeValue(columnFilters, columnKey, mode) {
  const existing = columnFilters[columnKey];
  if (!existing?.keywords?.size) return undefined;
  return { keywords: existing.keywords, mode };
}

/**
 * True if `value` is already among a keyword-cloud filter's `keywords`, compared
 * the same case-/quote-insensitive way the pill highlight and toggle use (via
 * `normalizeKeywordQuery`). The unified keyword editor uses this for its
 * checkbox state so a checked row and a lit pill always agree on what counts as
 * "the same keyword".
 *
 * @param {Set<string>|undefined} keywords - a filter's stored keyword set
 * @param {unknown} value - the candidate keyword (bare, unquoted)
 * @returns {boolean}
 */
export function isKeywordSelected(keywords, value) {
  if (!keywords || value == null || value === '') return false;
  const target = normalizeKeywordQuery(value).needle;
  for (const raw of keywords) {
    if (normalizeKeywordQuery(raw).needle === target) return true;
  }
  return false;
}

/**
 * The subset of `columnFilters` that came from clicking pills (the keyword-cloud
 * columns), so the chip rail surfaces pill filters without pulling in the
 * header-driven numeric / situation / location filters (which keep their own
 * ⌄ affordance).
 */
export function pickPillColumnFilters(columnFilters) {
  const subset = {};
  for (const key of KEYWORD_CLOUD_COLUMNS) {
    if (columnFilters[key]) subset[key] = columnFilters[key];
  }
  return subset;
}

/**
 * Build the pill-filter store actions (`togglePillFilter`, `setPillFilterMode`)
 * bound to a MobX store accessor, so both views' `useLocalObservable` stores can
 * spread them in identically.
 *
 * `getStore` is a thunk returning the observable store — deferred rather than
 * the store itself so this can be spread into the initializer object before the
 * `state` const it closes over has been assigned.
 *
 * `defaultMode` is the match mode a freshly-created keyword filter starts in.
 * The store passes it so the env-dependent choice (All/AND today, Any/OR under
 * the unified-filter flag) stays at the boundary and out of the pure helpers.
 */
export function createPillFilterActions(getStore, { defaultMode = DEFAULT_KEYWORD_MODE } = {}) {
  return {
    togglePillFilter(columnKey, value) {
      const store = getStore();
      const next = nextPillToggleValue(store.columnFilters, columnKey, value, defaultMode);
      if (next !== undefined) store.setColumnFilter(columnKey, next);
    },
    setPillFilterMode(columnKey, mode) {
      const store = getStore();
      const next = nextPillModeValue(store.columnFilters, columnKey, mode);
      if (next !== undefined) store.setColumnFilter(columnKey, next);
    }
  };
}
